// src/admin/CreateInstitution/CreateInstitution.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/config.js";

const initialForm = {
  name: "",
  abrev: "",
  image_path: "",
  path_base: "https://sismaster.perufedup.com/writable/uploads/",
  idaux: "",
};

const CreateInstitution = () => {
  const [showModal, setShowModal] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState(initialForm);
  const [selectedInstitution, setSelectedInstitution] = useState(null); // objeto o null
  const [mode, setMode] = useState("create"); // "create" | "edit"
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const openModal = async () => {
    setShowModal(true);
    setErrorMsg("");
    setSuccessMsg("");
    setForm(initialForm);
    setSelectedInstitution(null);
    setMode("create");
    await fetchInstitutions();
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(initialForm);
    setSelectedInstitution(null);
    setSearchTerm("");
    setErrorMsg("");
    setSuccessMsg("");
  };

  const fetchInstitutions = async () => {
    try {
      setLoadingList(true);
      const { data } = await axios.get(`${API_BASE_URL}/institutions`);
      setInstitutions(data || []);
      setFilteredInstitutions(data || []);
    } catch (error) {
      console.error("Error al cargar instituciones:", error);
      setErrorMsg("No se pudieron cargar las instituciones.");
    } finally {
      setLoadingList(false);
    }
  };

  // Filtro local por nombre/abrev
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = institutions.filter((inst) => {
      const name = inst.name ? inst.name.toLowerCase() : "";
      const abrev = inst.abrev ? inst.abrev.toLowerCase() : "";
      return name.includes(term) || abrev.includes(term);
    });
    setFilteredInstitutions(filtered);
  }, [searchTerm, institutions]);

  const handleSelectInstitution = (inst) => {
    setSelectedInstitution(inst);
    setMode("edit");
    setErrorMsg("");
    setSuccessMsg("");

    setForm({
      name: inst.name || "",
      abrev: inst.abrev || "",
      image_path: inst.image_path || "",
      path_base: inst.path_base || "https://sismaster.perufedup.com/writable/uploads/",
      idaux: inst.idaux !== null && inst.idaux !== undefined ? String(inst.idaux) : "",
    });
  };

  const handleNewInstitution = () => {
    setSelectedInstitution(null);
    setMode("create");
    setErrorMsg("");
    setSuccessMsg("");
    setForm(initialForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const payload = {
        name: form.name.trim(),
        abrev: form.abrev.trim(),
        image_path: form.image_path.trim(),
        path_base: form.path_base.trim(),
        idaux:
          form.idaux === "" || form.idaux === null
            ? null
            : Number.isNaN(Number(form.idaux))
            ? null
            : Number(form.idaux),
      };

      if (!payload.name) {
        setErrorMsg("El nombre es obligatorio.");
        setSaving(false);
        return;
      }

      if (mode === "create") {
        // Crear nueva institución
        await axios.post(`${API_BASE_URL}/postinstitution`, payload);
        setSuccessMsg("Institución creada correctamente.");
      } else if (mode === "edit" && selectedInstitution) {
        // Actualizar institución existente
        await axios.patch(`${API_BASE_URL}/updateinstitution`, {
          idinstitution: selectedInstitution.idinstitution,
          ...payload,
        });
        setSuccessMsg("Institución actualizada correctamente.");
      }

      // Refrescar lista para tener datos actualizados
      await fetchInstitutions();
    } catch (error) {
      console.error("Error al guardar institución:", error);
      setErrorMsg(
        error?.response?.data?.msg ||
          "Ocurrió un error al guardar la institución."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full">
      {/* Card principal */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-800">
            Crear / Editar Institución
          </h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Gestiona las instituciones: crea nuevas o actualiza abreviaturas,
          logos y configuración auxiliar.
        </p>
        <button
          type="button"
          onClick={openModal}
          className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          Abrir gestor de instituciones
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {mode === "create"
                  ? "Crear nueva institución"
                  : `Editar institución${selectedInstitution ? `: ${selectedInstitution.abrev || selectedInstitution.name}` : ""
                  }`}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Estado general */}
            {errorMsg && (
              <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded">
                {successMsg}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Columna izquierda: buscador y lista */}
              <div className="md:col-span-1 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">
                    Buscar institución
                  </h4>
                  <button
                    type="button"
                    onClick={handleNewInstitution}
                    className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
                  >
                    Nueva
                  </button>
                </div>

                <input
                  type="text"
                  placeholder="Buscar por nombre o abreviatura..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full mb-3 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="max-h-72 overflow-y-auto border border-gray-100 rounded">
                  {loadingList ? (
                    <div className="p-3 text-sm text-gray-500">
                      Cargando instituciones...
                    </div>
                  ) : filteredInstitutions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">
                      No se encontraron instituciones.
                    </div>
                  ) : (
                    filteredInstitutions.map((inst) => (
                      <button
                        key={inst.idinstitution}
                        type="button"
                        onClick={() => handleSelectInstitution(inst)}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                          selectedInstitution &&
                          selectedInstitution.idinstitution ===
                            inst.idinstitution
                            ? "bg-blue-100"
                            : ""
                        }`}
                      >
                        <div className="font-medium text-gray-800">
                          {inst.abrev || "Sin abrev."}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {inst.name}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Columna derecha: formulario */}
              <div className="md:col-span-2 border border-gray-200 rounded-lg p-4">
                <form onSubmit={handleSave} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nombre (name) *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nombre completo de la institución"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Abreviatura (abrev)
                    </label>
                    <input
                      type="text"
                      name="abrev"
                      value={form.abrev}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ej: UNI, PUCP, FEDUP"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Ruta de imagen (image_path)
                    </label>
                    <input
                      type="text"
                      name="image_path"
                      value={form.image_path}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ej: logos/uni.png"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Normalmente se concatena con <code>path_base</code>.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Path base (path_base)
                    </label>
                    <input
                      type="text"
                      name="path_base"
                      value={form.path_base}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      URL base para las imágenes. Si no necesitas cambiarla,
                      deja el valor por defecto.
                    </p>
                  </div>

                  <div className="max-w-xs">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      ID auxiliar (idaux)
                    </label>
                    <input
                      type="number"
                      name="idaux"
                      value={form.idaux}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Opcional (mapping externo)"
                    />
                  </div>

                  <div className="pt-2 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 text-sm rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving
                        ? "Guardando..."
                        : mode === "create"
                        ? "Crear institución"
                        : "Guardar cambios"}
                    </button>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInstitution;